import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { GridSubAction } from '@shared/ag-grid/component/sub-action/sub-action.enum';
import { GridState } from '@shared/ag-grid/gird-state';
import { ActionType } from '@shared/components/files/shared/action-type.enum';
import { DOCUMENT_TAG_TYPE } from '@shared/components/files/shared/document-tag-type.enum';
import { DownloadService } from '@shared/components/files/shared/download.service';
import { FolderService } from '@shared/components/files/shared/folder.service';
import { FilesActions } from '@shared/components/files/shared/models/action.model';
import { DocTag } from '@shared/components/files/shared/models/doc-tag.model';
import { TagType } from '@shared/components/files/shared/models/tag-type.model';
import { EventService } from '@shared/event.service';
import { User } from '@user/user.model';
import { UserService } from '@user/user.service';
import { ColumnApi, GridOptions } from 'ag-grid-community';
import { DateTime } from 'luxon';
import { Observable, Subject, Subscription } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { ActionService } from '../shared/action.service';
import { DocumentDataService } from '../shared/document-data.service';
import { FilesActionRuleService } from '../shared/files-action-rule.service';
import { FilesService } from '../shared/files.service';
import { AssociationAddComponent } from './action-components/association/add/add.component';
import { AssociationDeleteComponent } from './action-components/association/delete/delete.component';
import { CopyComponent } from './action-components/copy/copy.component';
import { CreateFolderComponent } from './action-components/create-folder/create-folder.component';
import { DeleteComponent } from './action-components/delete/delete.component';
import { EditNameComponent } from './action-components/edit-name/edit-name.component';
import { TagAddComponent } from './action-components/tag/add/add.component';
import { TagDeleteComponent } from './action-components/tag/delete/delete.component';
import { UploaderComponent } from './action-components/uploader/uploader.component';
import { FilesGridSettingService } from './files-grid-setting.service';
import { FilesPreviewComponent } from '../shared/files-preview/files-preview.component';

interface FilesContextMenuItem {
  actionType: ActionType;
  label: string;
  targetNode?: any;
}

@Component({
  selector: 'oes-files',
  templateUrl: './files.component.html',
  styleUrls: ['./files.component.scss'],
  providers: [FilesGridSettingService],
})
export class FilesComponent implements OnInit, OnDestroy, OnChanges {
  private static readonly ROOT_DROP_ALLOWED_CLASS = 'files-root-drop-active';

  @Input() tagTypes: TagType[];
  @Input() accessControl: FilesActions;
  @Input() anotherOrganizationId: string;
  @Input() storageKey = 'files-main-1';
  @Input() removeHeight: string = '227px';
  @Input() autoHeightOverride: boolean = false;
  @Input() uploadExtraMessage: string;
  @Input() reRender: boolean = false;
  @Input() printable = false;
  @Input() events: Observable<any>;
  @Input() title: string;
  @Input() sharedFiles: boolean = false;
  @Input() childDataRoom: boolean = true;
  @Input() ownerOrganizationId: string;
  @Input() requireOwnerOrganizationSelection = false;

  @Output() uploadProcessCompleted: EventEmitter<File[]> = new EventEmitter<File[]>();
  @Output() documentsLoaded: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild(CreateFolderComponent, { static: false })
  public createFolder: CreateFolderComponent;
  @ViewChild('copyModalDialog', { static: false })
  public copyModalDialog: CopyComponent;
  @ViewChild('moveModalDialog', { static: false })
  public moveModalDialog: CopyComponent;
  @ViewChild(AssociationAddComponent, { static: false })
  public associationAddDialog: AssociationAddComponent;
  @ViewChild(AssociationDeleteComponent, { static: false })
  public associationDeleteDialog: AssociationDeleteComponent;
  @ViewChild(TagAddComponent, { static: false })
  public tagAddDialog: TagAddComponent;
  @ViewChild(TagDeleteComponent, { static: false })
  public tagDeleteDialog: TagDeleteComponent;
  @ViewChild(DeleteComponent, { static: false })
  public deleteDialog: DeleteComponent;
  @ViewChild(UploaderComponent, { static: false })
  public uploadDialog: UploaderComponent;
  @ViewChild(EditNameComponent, { static: false })
  public editNameDialog: EditNameComponent;
  @ViewChild(FilesPreviewComponent, { static: false })
  public previewDialog: FilesPreviewComponent;
  @ViewChild('contextMenu', { static: false })
  public contextMenu: ElementRef<HTMLElement>;
  @ViewChild('topDropTarget', { static: false })
  public topDropTarget: ElementRef<HTMLElement>;
  @ViewChild('bottomDropTarget', { static: false })
  public bottomDropTarget: ElementRef<HTMLElement>;
  @ViewChildren('contextMenuItemButton')
  public contextMenuItemButtons: QueryList<ElementRef<HTMLButtonElement>>;

  _tagTypes: TagType[];

  contextMenuItems: FilesContextMenuItem[] = [];
  contextMenuPosition = { top: 0, left: 0 };
  contextMenuVisible = false;
  columnApi: ColumnApi;
  dataCompleted = false;
  defaultGridState: GridState;
  docPathList: string[];
  eventsSubscription: Subscription;
  expandState: 'expanded' | 'collapsed' | 'indeterminate' = 'expanded';
  selectState: 'none' | 'all' | 'indeterminate' = 'none';
  gridApi: any; // keep any to use rowsToDisplay
  gridOptions: GridOptions;
  gridState: GridState;
  isDraggingRows = false;
  organizationId: string;
  activeRootDropZone: 'top' | 'bottom' | undefined;
  bottomDropTargetOffset = 0;
  topDropTargetOffset = 104;

  private activeDropTargetRowId: string | undefined;
  private actionTargetNodeOverride: any;
  activeContextMenuIndex = 0;
  private currentDragEvent: any;
  private dragSelectionMode: 'selection' | 'single' | undefined;
  private isSelectionMutationInProgress = false;
  private preserveContextMenuDuringSelectionSync = false;
  private selectionAnchorRowId: string | undefined;
  private skipNextRowDragEnd = false;
  private gridStateStorageKey: string;
  private message;
  private ngUnsubscribe: Subject<any> = new Subject();
  private targetOrganizationId: string;
  private user: User;

  constructor(
    private _filesService: FilesService,
    private _documentDataService: DocumentDataService,
    private _userService: UserService,
    public _filesGridSettingService: FilesGridSettingService,
    private _downloadService: DownloadService,
    private _eventService: EventService,
    private _translateService: TranslateService,
    private _actionService: ActionService,
    private _folderService: FolderService,
    private _filesActionRuleService: FilesActionRuleService
  ) {
    this.subscribeActionButton();
    this._translateService
      .get('data-room.dialogs')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((message) => {
        this.message = message;

        if (!this.title) {
          this.title = message.title;
        }
      });
  }

  ngOnInit() {
    this.gridStateStorageKey = this._filesGridSettingService.buildGridStateStorageKey('myFilesList-shared=' + this.sharedFiles);
    this._filesGridSettingService._gridStateStorageKey = this.gridStateStorageKey;
    this.eventsSubscription = this.events?.subscribe((event: any) => {
      this.reload();
    });
    this._filesService.clear();
    this.setActionControl();
    this._filesGridSettingService.allowDrag = this.accessControl.move;
    this._userService
      .getCurrentUser()
      .pipe(take(1))
      .subscribe((user: User) => {
        if (user) {
          this.user = user;
          this.organizationId = user.organization.id;
          this.buildTagTypes();
        }
      });
    this.gridOptions = this._filesGridSettingService.getGridOptions(
      this._tagTypes,
      this.printable,
      this.sharedFiles,
      this.childDataRoom
    );
    this.gridOptions.rowDragText = (params, dragItemCount) => this.getRowDragText(params, dragItemCount);
    this.gridOptions['context'] = {
      gridService: this,
    };
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.tagTypes) {
      this._tagTypes = this.tagTypes;
      if (this._tagTypes?.length > 0) {
        if (this.reRender) {
          this.loadSwitcher();
        }
      }
    }
    if (changes.ownerOrganizationId && !changes.ownerOrganizationId.firstChange && this._tagTypes && this.gridApi) {
      this.loadSwitcher();
    }
  }

  ngOnDestroy() {
    this.eventsSubscription?.unsubscribe();
    this.ngUnsubscribe.next(null);
    this.ngUnsubscribe.complete();
    this.updateRootDropGhostState(false);
  }

  // callback: reload button
  reload() {
    if (this.gridApi) {
      if (this._tagTypes && this._tagTypes[0].docTagType === DOCUMENT_TAG_TYPE.ORGANIZATION) {
        this.loadDocuments();
      } else {
        this.loadDocumentsByTag();
      }
    }
  }

  // callback: ag-grid
  onGridReady(params) {
    this.gridApi = params.api;
    this.columnApi = params.columnApi;
    if (this.autoHeightOverride) {
      this.gridApi.setDomLayout('autoHeight');
    }
    this._filesGridSettingService.setGridApi(params.api, params.columnApi);
    this.defaultGridState = this._filesGridSettingService.buildDefaultGridState();
    this.updateRootDropTargetOffsets();
    this.loadSwitcher();
  }

  // ag-grid callback: filter, sort and group
  gridStatusChanged(event, type) {
    if (this.gridApi && this.columnApi) {
      this.storeGridState();
      this.getFolders();
      this.syncSelectionState();
    }
  }

  storeGridState() {
    if (this.gridApi && this.columnApi) {
      this.gridState = this._filesGridSettingService.storeGridStateByApis(
        this.storageKey,
        this.gridApi,
        this.columnApi
      );
    }
  }

  toggleSelectAll() {
    if (this.selectState === 'all') {
      this.clearAll();
    } else {
      this.selectAll();
    }
  }

  selectAll() {
    this.runSelectionMutation(() => {
      // selectAllFiltered (not selectAll) so the selection honours the active column filter -
      // e.g. after filtering by a specific developer or project, only the filtered files are
      // selected. syncSelectionState() then flattens the selection to the matching file nodes.
      this.gridApi.selectAllFiltered();
      const firstSelected = this.gridApi.getDisplayedRowAtIndex(0);
      this.selectionAnchorRowId = firstSelected?.id;
    });
  }

  clearAll() {
    this.runSelectionMutation(() => {
      this.gridApi.deselectAll();
      this.selectionAnchorRowId = undefined;
    });
  }

  toggleExpand() {
    if (this.expandState === 'expanded') {
      this.collapseAll();
    } else {
      this.expandAll();
      this.expandState = 'expanded';
    }
  }

  expandAll() {
    this.gridApi.expandAll();
    this._filesGridSettingService.saveAllOpenState(true);
    this.expandState = 'expanded';
  }

  collapseAll() {
    this.gridApi.collapseAll();
    this._filesGridSettingService.saveAllOpenState(false);
    this.expandState = 'collapsed';
  }

  // Callback from FileTagFilterComponent
  inComponentSelectionChanged(_event: any) {
    this.syncSelectionState();
  }

  onRowClicked(event) {
    if (!this.gridApi || !event?.node) {
      return;
    }

    if (typeof event?.event?.button === 'number' && event.event.button !== 0) {
      return;
    }

    const isModifierClick = event?.event?.metaKey || event?.event?.ctrlKey;
    const isShiftClick = event?.event?.shiftKey;

    if (isShiftClick) {
      this.selectVisibleRange(event.node);
      return;
    }

    if (isModifierClick) {
      this.closeContextMenu();
      this.runSelectionMutation(() => {
        event.node.setSelected(!event.node.isSelected());
      });
      return;
    }

    this.closeContextMenu();
    this.selectOnlyNode(event.node);
  }

  onCellContextMenu(event) {
    if (!this.gridApi || !event?.node) {
      return;
    }

    event.event?.preventDefault();

    if (!event.node.isSelected()) {
      this.preserveContextMenuDuringSelectionSync = true;
      this.selectOnlyNode(event.node);
    } else {
      this.syncSelectionState();
    }

    this.openContextMenu(event.node, event.event?.clientX || 0, event.event?.clientY || 0);
  }

  onGridSelectionChanged() {
    if (!this.isSelectionMutationInProgress) {
      this.syncSelectionState();
    }
  }

  onStateChange(event) {
    this._filesGridSettingService.saveCloseState({
      id: event?.node?.id,
      expanded: event?.node?.expanded,
    });
    this.expandState = this._filesGridSettingService.findExpandState();
  }

  subAction(action: GridSubAction) {
    switch (action) {
      case GridSubAction.exportList:
        this.exportCsv();
        break;
      case GridSubAction.reload:
        this.reload();
        break;
      case GridSubAction.clearFilter:
        this.columnApi.getColumns().forEach((column) => {
          const colDef = column.getColDef();
          if (colDef.filter === 'fileTagFilterComponent') {
            this.gridOptions.api.getFilterInstance(colDef.field).setModel(null);
          }
        });
        this.gridOptions.api.onFilterChanged();
        this._filesGridSettingService.clearStoredGridState(this.storageKey);
        this.clearAll();
        break;
    }
  }

  onRowDragEnd(event) {
    if (this.skipNextRowDragEnd) {
      this.skipNextRowDragEnd = false;
      this.finalizeDragInteraction();
      return;
    }

    const resolvedDragEvent = this.resolveDragTargetEvent(event);
    this.executeDragMove(resolvedDragEvent);
    this.finalizeDragInteraction();
  }

  private loadDocuments() {
    if (this.gridApi) {
      this.clearAll();
    }

    if (this.sharedFiles) {
      if (this.requireOwnerOrganizationSelection) {
        if (!this.ownerOrganizationId) {
          this.gridApi.setRowData([]);
          this.gridApi.showNoRowsOverlay();
          this.dataCompleted = true;
          this.documentsLoaded.emit();
          return;
        }
        this.gridApi.setRowData([]);
        this.gridApi.showLoadingOverlay();
        this._documentDataService
          .getSharedListByOwnerOrganization(this.targetOrganizationId, this.ownerOrganizationId)
          .pipe(take(1))
          .subscribe({
            next: (docs: any[]) => {
              this.extractDocs(docs);
              this.documentsLoaded.emit();
            },
            error: () => {
              this.gridApi.hideOverlay();
              this.documentsLoaded.emit();
              this._eventService.error(this.message['error']);
            },
          });
      } else {
        this._documentDataService
          .getSharedList(this.targetOrganizationId)
          .pipe(take(1))
          .subscribe({
            next: (docs: any[]) => {
              this.extractDocs(docs);
            },
            error: () => {
              this.gridApi.hideOverlay();
              this._eventService.error(this.message['error']);
            },
          });
      }
    } else if (this.targetOrganizationId) {
      this._documentDataService
        .getList(this.targetOrganizationId)
        .pipe(take(1))
        .subscribe((docs: any[]) => {
          this.extractDocs(docs);
        });
    }
  }

  // Load documents by Tag (for readonly page)
  private loadDocumentsByTag() {
    if (this.gridApi) {
      this.gridApi.deselectAll();
    }
    this.selectionAnchorRowId = undefined;
    this._filesService.selected([]);
    this._filesService.highlighted([]);

    let tagType: TagType;
    if (this._tagTypes?.length === 1) {
      tagType = this._tagTypes[0];
    } else if (this._tagTypes?.length === 2) {
      tagType = this._tagTypes.filter((item) => {
        return (
          item.docTagType === DOCUMENT_TAG_TYPE.DISTRIBUTION_DESIGN ||
          item.docTagType === DOCUMENT_TAG_TYPE.PRE_QUALIFICATION ||
          item.docTagType === DOCUMENT_TAG_TYPE.MILESTONE ||
          item.docTagType === DOCUMENT_TAG_TYPE.PARTICIPANT_REPORT ||
          item.docTagType === DOCUMENT_TAG_TYPE.DEVELOPER_INFORMATION ||
          item.docTagType === DOCUMENT_TAG_TYPE.VENDOR_INFORMATION
        );
      })[0];
    }
    if (tagType && this.targetOrganizationId) {
      const docTag: DocTag = {
        tagName: tagType.name,
        taggedItemId: tagType.docTagItemId,
        type: tagType.docTagType,
        systemDocumentType: tagType.systemDocumentType,
      };
      const includeSponsorDocs = tagType.docTagType === DOCUMENT_TAG_TYPE.PROJECT;
      this._documentDataService
        .getListByTag(this.targetOrganizationId, docTag, includeSponsorDocs)
        .pipe(take(1))
        .subscribe((docs: any[]) => {
          this.extractDocs(docs);
        });
    }
  }

  private extractDocs(docs: any[]) {
    this.gridApi.showLoadingOverlay();
    this.selectionAnchorRowId = undefined;
    if (docs?.length > 0) {
      const rowData = this.flatten(docs);
      this.docPathList = rowData.map((doc) => doc.path);
      this._filesService.tagList = this.generateTagList(rowData, 'tags');
      this._filesService.associationList = this.generateTagList(rowData, 'associations');
      this._filesService.programList = this.generateTagList(rowData, 'programTags');
      this._filesService.portfolioList = this.generateTagList(rowData, 'projectGroupTags');
      this._filesService.ciPortfolioList = this.generateTagList(rowData, 'projectSetPortfolioTags');
      this._filesService.projectList = this.generateTagList(rowData, 'projectTags');
      this._filesGridSettingService.loadCloseState();
      this.gridApi.setRowData(rowData);
      this._filesGridSettingService.applyStoredGridState(
        this.storageKey,
        this.defaultGridState
      );
      this.gridApi.hideOverlay();
    } else {
      this.gridApi.setRowData([]);
      this.gridApi.showNoRowsOverlay();
    }
    this.getFolders();
    this.dataCompleted = true;
  }

  private getFolders() {
    if (this.gridApi) {
      const rowData = this.gridApi.getModel();
      this._folderService.allFolders = this.getFoldersFromRows(Object.values(rowData.nodeManager.allNodesMap));
      this._folderService.folders = this.getFoldersFromRows(rowData.rowsToDisplay);
    }
  }

  getFoldersFromRows(rows: any) {
    return rows.filter((item) => {
      return (item.data &&
              item.data.documentKey &&
              item.data.documentKey.docType === 'PATH');
    })
    .map((item) => {
      return item.data;
    });
  }

  private flatten(rawData: any[]): any[] {
    const filteredData = rawData.map((item) => {
      return {
        path: item.path ? item.path.replace(/^\//gi, '') : '', // remove the first '/'
        associations: item.documentTags.filter((documentTag) => documentTag.tagType !== DOCUMENT_TAG_TYPE.USER),
        tags: item.documentTags.filter((documentTag) => documentTag.tagType === DOCUMENT_TAG_TYPE.USER),
        size: item.size,
        created: item.created,
        id: item.id,
        documentKey: item.documentKey, // need documentKey for delete folder/file
        formUpload: item.formUpload,
        allowDelete: item.allowDelete,
        allowRename: item.allowRename,
        programTags: item.programTags,
        projectTags: item.projectTags,
        projectGroupTags: item.projectGroupTags,
        projectSetPortfolioTags: item.projectSetPortfolioTags,
        sharedDisplayPath: item.sharedDisplayPath,
      };
    });
    return this.sort(filteredData, 'path');
  }

  private generateTagList(rowData: any, key: string) {
    let tags = [];
    rowData.forEach((data) => {
      let newTags = null;
      if (data && data[key]) {
        newTags = data[key]
          .filter((documentTag) => {
            if (tags && tags.length > 0) {
              // check duplication
              const index = tags.findIndex((item) => item.id === documentTag.id);
              if (index === -1) {
                return documentTag;
              }
            } else {
              return documentTag;
            }
          })
          .map((documentTag) => {
            return { id: documentTag.id,
                     tag: documentTag.tag };
          });
        tags = [...tags, ...newTags];
      }
    });
    return tags;
  }

  private sort(data, key) {
    return data.sort((a, b) => {
      if (a[key] < b[key]) {
        return -1;
      }
      if (a[key] > b[key]) {
        return 1;
      }
      return 0;
    });
  }

  private buildTagTypes() {
    if (!this._tagTypes || this._tagTypes.length === 0) {
      this.setOrganizationId();
      this._tagTypes = [
        { docTagItemId: this.targetOrganizationId,
          docTagType: DOCUMENT_TAG_TYPE.ORGANIZATION },
      ];
    }
  }

  private setOrganizationId() {
    if (
      (this.user && !this.anotherOrganizationId) ||
      this.anotherOrganizationId === ''
    ) {
      this.targetOrganizationId = this.user.organization.id;
    } else {
      this.targetOrganizationId = this.anotherOrganizationId;
    }
  }

  private setActionControl() {
    if (!this.accessControl) {
      if (this.sharedFiles) {
        this.accessControl = new FilesActions({
          copy: false,
          create: false,
          delete: false,
          download: true,
          move: false,
          upload: false,
          tag: false,
          editName: false,
          preview: true,
        });
      } else {
        this.accessControl = new FilesActions({
          copy: true,
          create: true,
          delete: true,
          download: true,
          move: true,
          upload: true,
          tag: true,
          editName: true,
          preview: true,
        });
      }
    }
  }

  private subscribeActionButton() {
    this._filesService.actions$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((actionType: ActionType) => {
        const actionTargetNodeOverride = this.actionTargetNodeOverride;
        this.actionTargetNodeOverride = undefined;

        switch (actionType) {
          // TODO: documents/path needs to support tags
          case ActionType.createFolder: {
            this.createFolder?.show();
            break;
          }
          case ActionType.move: {
            this.moveModalDialog?.show();
            break;
          }
          case ActionType.copy: {
            this.copyModalDialog?.show();
            break;
          }
          case ActionType.download: {
            this._downloadService.download(this.targetOrganizationId);
            break;
          }
          case ActionType.upload: {
            this.uploadDialog?.show();
            break;
          }
          case ActionType.delete: {
            this.deleteDialog?.show();
            break;
          }
          case ActionType.associationAdd: {
            this.associationAddDialog?.show();
            break;
          }
          case ActionType.associationDelete: {
            this.associationDeleteDialog?.show();
            break;
          }
          case ActionType.tagAdd: {
            this.tagAddDialog?.show();
            break;
          }
          case ActionType.tagDelete: {
            this.tagDeleteDialog?.show();
            break;
          }
          case ActionType.editName: {
            this.editNameDialog?.show(actionTargetNodeOverride);
            break;
          }
          case ActionType.completed: {
            this.reload();
            break;
          }
          case ActionType.export: {
            this.exportCsv();
            break;
          }
          case ActionType.preview: {
            this.previewDialog?.show(this.targetOrganizationId, actionTargetNodeOverride || this._filesService.getSelectedNodes()?.[0]);
            break;
          }
        }
      });
  }

  private loadSwitcher() {
    this.setOrganizationId();
    if (
      this._tagTypes &&
      this._tagTypes[0].docTagType === DOCUMENT_TAG_TYPE.ORGANIZATION
    ) {
      this.loadDocuments();
    } else {
      this.loadDocumentsByTag();
    }
  }

  private exportCsv() {
    const categories = this._tagTypes.map((item) => item.name).join('-');
    const postfix = categories ? '(' + categories + ') ' : '';
    const fileName = 'Files ' + postfix + DateTime.local().toSQLDate();
    this._filesGridSettingService.exportCsv(this.gridApi, fileName, false, (value) => {
        if (value.column.colDef.headerName === this._translateService.instant('data-room.column.tree')) {
          return value.node.data.path;
        }
        switch (value.column.colDef.field) {
          case 'associations':
            return value.node.data ? value.node.data.associations
              .map((document) => document.tag)
              .join('|') : '';
          case 'tags':
            return value.node.data ? value.node.data.tags.map((document) => document.tag).join('|') : '';
          case 'size':
            return value.value ? this._filesGridSettingService.formatFileSize(value) : '0 KB';
          default:
            return value.value;
        }
      }
    );
  }

  onRowDragEnter(event) {
    this.isDraggingRows = true;
    this.currentDragEvent = event;
    this.updateRootDropTargetOffsets();
    this.dragSelectionMode = event?.node?.isSelected() ? 'selection' : 'single';
    if (this.dragSelectionMode === 'single') {
      this.selectOnlyNode(event.node);
    }
    this.updateDropTarget(event);
  }

  onRowDragMove(event) {
    this.currentDragEvent = event;
    if (this.updateRootDropTargetHover(event?.event?.clientX, event?.event?.clientY)) {
      return;
    }
    this.updateDropTarget(event);
  }

  onRowDragLeave() {
    this.setActiveDropTarget(undefined);
  }

  onTopDropTargetEnter() {
    if (!this.isDraggingRows) {
      return;
    }

    this.setRootDropTarget('top');
  }

  onTopDropTargetLeave() {
    this.clearRootDropTarget('top');
  }

  onBottomDropTargetEnter() {
    if (!this.isDraggingRows) {
      return;
    }

    this.setRootDropTarget('bottom');
  }

  onBottomDropTargetLeave() {
    this.clearRootDropTarget('bottom');
  }

  onGridShellClick(event: MouseEvent) {
    if (!this.gridApi) {
      return;
    }

    const target = event.target as HTMLElement;
    if (!target) {
      return;
    }

    const isGridBodyClick = !!target.closest(
      '.ag-root-wrapper-body, .ag-body-viewport, .ag-center-cols-viewport, .ag-center-cols-container, .ag-pinned-left-cols-container'
    );
    const clickedRow = !!target.closest('.ag-row');
    const clickedHeader = !!target.closest('.ag-header, .ag-floating-filter, .ag-paging-panel');
    const clickedDropTarget = !!target.closest('.files-root-drop-target');

    if (isGridBodyClick && !clickedRow && !clickedHeader && !clickedDropTarget) {
      this.clearSelection();
    }
  }

  onCellKeyDown(event) {
    const keyboardEvent = event?.event;
    if (!keyboardEvent || !event?.node) {
      return;
    }

    const isContextMenuKey = keyboardEvent.key === 'ContextMenu'
      || (keyboardEvent.shiftKey && keyboardEvent.key === 'F10');

    if (!isContextMenuKey) {
      return;
    }

    keyboardEvent.preventDefault();
    if (!event.node.isSelected()) {
      this.selectOnlyNode(event.node);
    } else {
      this.syncSelectionState();
    }

    const targetElement = keyboardEvent.target as HTMLElement;
    const rect = targetElement?.getBoundingClientRect?.();
    const left = rect ? rect.left + Math.min(rect.width / 2, 24) : 0;
    const top = rect ? rect.top + Math.min(rect.height / 2, 24) : 0;
    this.openContextMenu(event.node, left, top);
  }

  isActiveDropTarget(node): boolean {
    return !!node?.id && node.id === this.activeDropTargetRowId;
  }

  getRowDragText(params, dragItemCount: number): string {
    const highlightedNodes = this._filesService.getHighlightedNodes() || [];
    const selectedNodes = this._filesService.getSelectedNodes() || [];
    const selectionCount = ((this.dragSelectionMode === 'selection'
      || (params?.rowNode?.isSelected?.() && highlightedNodes.length > 1))
      ? selectedNodes.length
      : params?.rowNode?.allLeafChildren?.length || dragItemCount || 1) || 1;
    return this.buildCountLabel(selectionCount, 'files.drag');
  }

  private selectOnlyNode(node, updateAnchor = true) {
    if (!node) {
      return;
    }

    this.runSelectionMutation(() => {
      const isAlreadyOnlySelected = this.isOnlySelectedNode(node);
      if (!isAlreadyOnlySelected) {
        this.gridApi.deselectAll();
        node.setSelected(true);
      }
      if (updateAnchor) {
        this.selectionAnchorRowId = node.id;
      }
    });
  }

  private selectVisibleRange(node) {
    const anchorNode = this.selectionAnchorRowId ? this.gridApi.getRowNode(this.selectionAnchorRowId) : undefined;
    if (!anchorNode || anchorNode.rowIndex === null || node?.rowIndex === null) {
      this.selectOnlyNode(node);
      return;
    }

    const startIndex = Math.min(anchorNode.rowIndex, node.rowIndex);
    const endIndex = Math.max(anchorNode.rowIndex, node.rowIndex);

    this.runSelectionMutation(() => {
      this.gridApi.deselectAll();
      for (let rowIndex = startIndex; rowIndex <= endIndex; rowIndex++) {
        const displayedRow = this.gridApi.getDisplayedRowAtIndex(rowIndex);
        displayedRow?.setSelected(true);
      }
    });
  }

  private isOnlySelectedNode(node): boolean {
    const selectedNodes = this.gridApi?.getSelectedNodes() || [];
    return selectedNodes.length === 1 && selectedNodes[0]?.id === node.id;
  }

  private runSelectionMutation(callback: () => void) {
    this.isSelectionMutationInProgress = true;
    callback();
    this.isSelectionMutationInProgress = false;
    this.syncSelectionState();
  }

  private syncSelectionState() {
    if (!this.gridApi) {
      return;
    }

    if (this.preserveContextMenuDuringSelectionSync) {
      this.preserveContextMenuDuringSelectionSync = false;
    } else {
      this.closeContextMenu();
    }

    const selectedNodes = [];
    const collectFilteredLeaves = (node) => {
      // Folders descend into the children that passed the active filter; files
      // are added to the flat selection. `childrenAfterFilter` respects the
      // current column filter and - unlike `rowIndex` - is still populated when
      // the folder is collapsed, so a selected folder keeps contributing its
      // files whether it is expanded or minimized.
      //
      // Folders are detected by document type (PATH) or by simply having
      // filter-visible children: the AG-Grid `group` flag is unreliable in this
      // tree (nested folders report group:false), and relying on it would let
      // folder nodes leak into the file selection - which keeps the Download
      // button enabled but produces an empty download payload downstream.
      const docType = node?.data?.documentKey?.docType;
      const children = node.childrenAfterFilter;
      if (docType === 'PATH' || (children && children.length > 0)) {
        (children || []).forEach(collectFilteredLeaves);
        return;
      }
      if (!selectedNodes.includes(node)) {
        selectedNodes.push(node);
      }
    };
    this.gridApi.getSelectedNodes().forEach(collectFilteredLeaves);

    this._filesService.highlighted(this.gridApi.getSelectedNodes());
    this._filesService.selected(selectedNodes);
    this.updateSelectState(selectedNodes.length);
  }

  // Reflects the "Select all files" checkbox state against the files that pass the current filter:
  // 'none' when nothing is selected, 'all' when every filtered file is selected, otherwise
  // 'indeterminate'. Files are identified by document type (PATH folders are ignored), consistent
  // with how the selection itself is flattened.
  private updateSelectState(selectedFileCount: number) {
    if (!this.gridApi) {
      this.selectState = 'none';
      return;
    }
    let filteredFileCount = 0;
    this.gridApi.forEachNodeAfterFilter((node) => {
      if (node?.data?.documentKey?.docType === 'FILE') {
        filteredFileCount++;
      }
    });

    if (selectedFileCount === 0) {
      this.selectState = 'none';
    } else if (filteredFileCount > 0 && selectedFileCount >= filteredFileCount) {
      this.selectState = 'all';
    } else {
      this.selectState = 'indeterminate';
    }
  }

  private updateDropTarget(event) {
    if (!this._actionService.validateDragMove(event)) {
      this.setActiveDropTarget(undefined);
      return;
    }

    this.setActiveDropTarget(event.overNode?.id);
  }

  private setActiveDropTarget(rowId: string | undefined) {
    const normalizedRowId = rowId || undefined;
    if (this.activeDropTargetRowId === normalizedRowId) {
      return;
    }

    const rowNodes = [this.activeDropTargetRowId, normalizedRowId]
      .filter(Boolean)
      .map((id) => this.gridApi?.getRowNode(id))
      .filter(Boolean);

    this.activeDropTargetRowId = normalizedRowId;
    if (normalizedRowId !== 'baseLevel') {
      this.activeRootDropZone = undefined;
    }
    this.updateRootDropGhostState(normalizedRowId === 'baseLevel');
    if (rowNodes.length > 0) {
      this.gridApi?.redrawRows({ rowNodes });
    }
  }

  private resolveDragTargetEvent(event) {
    if (this.activeDropTargetRowId === 'baseLevel') {
      return {
        ...event,
        overNode: { id: 'baseLevel' },
      };
    }

    if (this.activeDropTargetRowId) {
      const activeRowNode = this.gridApi?.getRowNode(this.activeDropTargetRowId);
      if (activeRowNode) {
        return {
          ...event,
          overNode: activeRowNode,
        };
      }
    }

    return event;
  }

  private executeDragMove(event) {
    if (!this._actionService.validateDragMove(event)) {
      return;
    }

    const selectedNodes = this.dragSelectionMode === 'selection'
      ? (this._filesService.getSelectedNodes() || event.node.allLeafChildren)
      : event.node.allLeafChildren;
    const selectedOption = this._actionService.getSelectedOption(event.overNode, this.organizationId);
    const requestItems = this._actionService.getRequestItems(selectedNodes, selectedOption);
    const destinationTagType = this._actionService.getDestinationTagType(selectedOption);
    this._documentDataService
      .moveCopyDocuments(requestItems, destinationTagType, 'move')
      .pipe(take(1))
      .subscribe(() => {
        this._eventService.success(this.message['moved']);
        this._filesService.clickActionButton(ActionType.completed);
      });
  }

  private finalizeDragInteraction() {
    this.isDraggingRows = false;
    this.activeRootDropZone = undefined;
    this.currentDragEvent = undefined;
    this.dragSelectionMode = undefined;
    this.setActiveDropTarget(undefined);
    this.updateRootDropGhostState(false);
  }

  private clearSelection() {
    this.closeContextMenu();
    this.selectionAnchorRowId = undefined;
    this.runSelectionMutation(() => {
      this.gridApi.deselectAll();
    });
  }

  private updateRootDropTargetOffsets() {
    const wrapperElement = document.querySelector('.ag-grid-header-sticky') as HTMLElement;
    const filesGrid = document.getElementById('filesGrid');
    const rowElements = filesGrid?.querySelectorAll(
      '.ag-center-cols-container .ag-row, .ag-pinned-left-cols-container .ag-row'
    );
    const firstRowElement = rowElements?.[0] as HTMLElement;
    const lastRowElement = rowElements?.[rowElements.length - 1] as HTMLElement;
    const headerElement = filesGrid?.querySelector('.ag-header') as HTMLElement;

    if (wrapperElement && firstRowElement) {
      const wrapperRect = wrapperElement.getBoundingClientRect();
      const firstRowRect = firstRowElement.getBoundingClientRect();
      this.topDropTargetOffset = Math.max(0, firstRowRect.top - wrapperRect.top - 7);
      if (lastRowElement) {
        const lastRowRect = lastRowElement.getBoundingClientRect();
        this.bottomDropTargetOffset = Math.max(this.topDropTargetOffset, lastRowRect.bottom - wrapperRect.top - 13);
      }
      return;
    }

    this.topDropTargetOffset = (headerElement?.offsetHeight || 96) + 8;
    this.bottomDropTargetOffset = this.topDropTargetOffset;
  }

  private updateRootDropTargetHover(clientX?: number, clientY?: number): boolean {
    if (!this.isDraggingRows || clientX == null || clientY == null) {
      return false;
    }

    const hoveredRootDropZone = this.getHoveredRootDropZone(clientX, clientY);
    if (hoveredRootDropZone) {
      this.setRootDropTarget(hoveredRootDropZone);
      return true;
    }

    if (this.activeDropTargetRowId === 'baseLevel') {
      this.setActiveDropTarget(undefined);
    }

    return false;
  }

  private getHoveredRootDropZone(clientX: number, clientY: number): 'top' | 'bottom' | undefined {
    const topTargetRect = this.topDropTarget?.nativeElement?.getBoundingClientRect();
    if (topTargetRect &&
      clientX >= topTargetRect.left &&
      clientX <= topTargetRect.right &&
      clientY >= topTargetRect.top &&
      clientY <= topTargetRect.bottom) {
      return 'top';
    }

    const bottomTargetRect = this.bottomDropTarget?.nativeElement?.getBoundingClientRect();
    if (bottomTargetRect &&
      clientX >= bottomTargetRect.left &&
      clientX <= bottomTargetRect.right &&
      clientY >= bottomTargetRect.top &&
      clientY <= bottomTargetRect.bottom) {
      return 'bottom';
    }

    return undefined;
  }

  private setRootDropTarget(position: 'top' | 'bottom') {
    this.activeRootDropZone = position;
    this.setActiveDropTarget('baseLevel');
  }

  private clearRootDropTarget(position: 'top' | 'bottom') {
    if (this.activeRootDropZone !== position) {
      return;
    }

    if (this.activeDropTargetRowId === 'baseLevel') {
      this.setActiveDropTarget(undefined);
    }
  }

  private updateRootDropGhostState(isAllowed: boolean) {
    document?.body?.classList.toggle(FilesComponent.ROOT_DROP_ALLOWED_CLASS, isAllowed);
  }

  private openContextMenu(rowNode, left: number, top: number) {
    const items = this.buildContextMenuItems(rowNode);
    if (items.length === 0) {
      this.closeContextMenu();
      return;
    }

    this.contextMenuItems = items;
    this.contextMenuVisible = true;
    this.activeContextMenuIndex = 0;
    this.contextMenuPosition = { left, top };

    setTimeout(() => {
      this.constrainContextMenuToViewport();
      this.focusContextMenuItem();
    });
  }

  executeContextMenuAction(item: FilesContextMenuItem) {
    this.closeContextMenu();
    this.actionTargetNodeOverride = item.targetNode;
    this._filesService.clickActionButton(item.actionType);
  }

  private buildContextMenuItems(rowNode): FilesContextMenuItem[] {
    if (!rowNode) {
      return [];
    }

    const selectedNodes = this._filesService.getSelectedNodes() || [];
    const highlightedNodes = this._filesService.getHighlightedNodes() || [];
    const targetHighlightedNodes = [rowNode];
    const targetSelectedNodes = rowNode.allLeafChildren || [rowNode];
    const itemCount = selectedNodes.length;
    const items: FilesContextMenuItem[] = [];

    if (this._filesActionRuleService.canPreview(this.accessControl, targetHighlightedNodes)) {
      items.push({
        actionType: ActionType.preview,
        label: this._translateService.instant('buttons.preview'),
        targetNode: rowNode,
      });
    }

    if (this._filesActionRuleService.canEditName(this.accessControl, targetHighlightedNodes, targetSelectedNodes)) {
      items.push({
        actionType: ActionType.editName,
        label: this._translateService.instant('buttons.edit-name'),
        targetNode: rowNode,
      });
    }

    if (this._filesActionRuleService.canMove(this.accessControl, selectedNodes)) {
      items.push({
        actionType: ActionType.move,
        label: this.buildBulkContextMenuLabel('buttons.move', itemCount),
      });
    }

    if (this._filesActionRuleService.canCopy(this.accessControl, selectedNodes)) {
      items.push({
        actionType: ActionType.copy,
        label: this.buildBulkContextMenuLabel('buttons.copy', itemCount),
      });
    }

    if (this._filesActionRuleService.canAssociationAdd(this.accessControl, highlightedNodes)) {
      items.push({
        actionType: ActionType.associationAdd,
        label: this.buildBulkContextMenuLabel('buttons.add-association', itemCount),
      });
    }

    if (this._filesActionRuleService.canAssociationDelete(this.accessControl, highlightedNodes, selectedNodes)) {
      items.push({
        actionType: ActionType.associationDelete,
        label: this.buildBulkContextMenuLabel('files.context-menu.remove-association', itemCount),
      });
    }

    if (this._filesActionRuleService.canTagAdd(this.accessControl, highlightedNodes)) {
      items.push({
        actionType: ActionType.tagAdd,
        label: this.buildBulkContextMenuLabel('buttons.add-tag', itemCount),
      });
    }

    if (this._filesActionRuleService.canTagDelete(this.accessControl, highlightedNodes)) {
      items.push({
        actionType: ActionType.tagDelete,
        label: this.buildBulkContextMenuLabel('files.context-menu.remove-tag', itemCount),
      });
    }

    return items;
  }

  private buildBulkContextMenuLabel(labelKey: string, itemCount: number): string {
    const label = this._translateService.instant(labelKey);
    return `${label} (${this.buildCountLabel(itemCount, 'files.context-menu')})`;
  }

  private buildCountLabel(itemCount: number, keyPrefix: string): string {
    return this._translateService.instant(
      itemCount === 1 ? `${keyPrefix}.item-count-one` : `${keyPrefix}.item-count-other`,
      { count: itemCount }
    );
  }

  private constrainContextMenuToViewport() {
    const menuElement = this.contextMenu?.nativeElement;
    if (!menuElement) {
      return;
    }

    const menuRect = menuElement.getBoundingClientRect();
    const margin = 8;
    const maxLeft = window.innerWidth - menuRect.width - margin;
    const maxTop = window.innerHeight - menuRect.height - margin;

    this.contextMenuPosition = {
      left: Math.max(margin, Math.min(this.contextMenuPosition.left, maxLeft)),
      top: Math.max(margin, Math.min(this.contextMenuPosition.top, maxTop)),
    };
  }

  private focusContextMenuItem() {
    const targetButton = this.contextMenuItemButtons?.get(this.activeContextMenuIndex)?.nativeElement;
    targetButton?.focus();
  }

  private moveContextMenuFocus(offset: number) {
    if (!this.contextMenuVisible || this.contextMenuItems.length === 0) {
      return;
    }

    const nextIndex = (this.activeContextMenuIndex + offset + this.contextMenuItems.length) % this.contextMenuItems.length;
    this.activeContextMenuIndex = nextIndex;
    this.focusContextMenuItem();
  }

  private closeContextMenu() {
    this.contextMenuVisible = false;
    this.contextMenuItems = [];
    this.activeContextMenuIndex = 0;
    this.actionTargetNodeOverride = undefined;
  }

  @HostListener('document:click')
  onDocumentClick() {
    if (this.contextMenuVisible) {
      this.closeContextMenu();
    }
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      if (this.contextMenuVisible) {
        this.closeContextMenu();
      } else if ((this.gridApi?.getSelectedNodes?.() || []).length > 0) {
        this.clearSelection();
      }
      return;
    }

    if (!this.contextMenuVisible) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.moveContextMenuFocus(1);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.moveContextMenuFocus(-1);
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      this.executeContextMenuAction(this.contextMenuItems[this.activeContextMenuIndex]);
    }
  }

  @HostListener('document:mousemove', ['$event'])
  onDocumentMouseMove(event: MouseEvent) {
    this.updateRootDropTargetHover(event.clientX, event.clientY);
  }

  @HostListener('document:mouseup', ['$event'])
  onDocumentMouseUp(_event: MouseEvent) {
    if (!this.isDraggingRows || this.activeDropTargetRowId !== 'baseLevel' || !this.currentDragEvent) {
      return;
    }

    this.skipNextRowDragEnd = true;
    this.executeDragMove({
      ...this.currentDragEvent,
      overNode: { id: 'baseLevel' },
    });
    this.finalizeDragInteraction();
  }
}
